export interface RenderMarkdownOptions {
  /** When set, a leading `#` heading that matches this title is omitted from the body. */
  pageTitle?: string;
  /** When true (default), `Last Updated:` lines are omitted from the body (show in page shell). */
  skipLastUpdated?: boolean;
}

function normalizeMarkdown(md: string): string {
  return md.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
}

function normalizeTitle(title: string): string {
  return title.trim().toLowerCase().replace(/^platform\s+/, '');
}

function titlesMatch(heading: string, pageTitle?: string): boolean {
  if (!pageTitle) return false;
  return normalizeTitle(heading) === normalizeTitle(pageTitle);
}

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function inlineMarkdown(text: string): string {
  let result = escapeHtml(text);
  result = result.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  result = result.replace(/__([^_]+)__/g, '<strong>$1</strong>');
  result = result.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
  );
  result = result.replace(
    /(https?:\/\/[^\s<]+)/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
  );
  return result;
}

/** Extracts the first `Last Updated:` line from legal markdown, if present. */
export function extractLastUpdated(md: string): string | null {
  for (const line of normalizeMarkdown(md).split('\n')) {
    const trimmed = line.trim();
    if (/^Last Updated:\s*.+/i.test(trimmed)) {
      return trimmed;
    }
  }
  return null;
}

/** Line-oriented markdown → HTML for legal pages (headings, paragraphs, links, lists, bold). */
export function renderSimpleMarkdown(md: string, options: RenderMarkdownOptions = {}): string {
  const { pageTitle, skipLastUpdated = true } = options;
  const lines = normalizeMarkdown(md).split('\n');
  const parts: string[] = [];
  let paragraphLines: string[] = [];
  let listItems: string[] = [];

  const flushParagraph = () => {
    if (paragraphLines.length === 0) return;
    const content = paragraphLines.map((l) => inlineMarkdown(l)).join('<br/>');
    parts.push(`<p>${content}</p>`);
    paragraphLines = [];
  };

  const flushList = () => {
    if (listItems.length === 0) return;
    parts.push(`<ul>${listItems.map((item) => `<li>${inlineMarkdown(item)}</li>`).join('')}</ul>`);
    listItems = [];
  };

  const skipLine = (text: string): boolean =>
    skipLastUpdated && /^Last Updated:\s*.+/i.test(text);

  for (const rawLine of lines) {
    const trimmed = rawLine.trim();

    if (!trimmed) {
      flushParagraph();
      flushList();
      continue;
    }

    if (skipLine(trimmed)) {
      flushParagraph();
      flushList();
      continue;
    }

    const headingMatch = trimmed.match(/^(#{1,4})\s+(.+)$/);
    if (headingMatch) {
      flushParagraph();
      flushList();
      const level = headingMatch[1].length;
      const text = headingMatch[2];

      if (level === 1 && titlesMatch(text, pageTitle)) {
        continue;
      }

      if (skipLine(text)) {
        continue;
      }

      if (level === 1) {
        parts.push(`<p><strong>${inlineMarkdown(text)}</strong></p>`);
      } else if (level === 2) {
        parts.push(`<h3 class="site-support-legal__section">${inlineMarkdown(text)}</h3>`);
      } else {
        parts.push(`<h4 class="site-support-legal__subsection">${inlineMarkdown(text)}</h4>`);
      }
      continue;
    }

    if (/^[-*]\s+/.test(trimmed)) {
      flushParagraph();
      listItems.push(trimmed.replace(/^[-*]\s+/, ''));
      continue;
    }

    flushList();
    paragraphLines.push(trimmed);
  }

  flushParagraph();
  flushList();

  return parts.join('');
}
